import { useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ActiveHandScreen } from '@/screens/ActiveHand';
import { ConsentScreen } from '@/screens/Consent';
import { CouldntBeGradedScreen } from '@/screens/CouldntBeGraded';
import { HandResolutionScreen } from '@/screens/HandResolution';
import { PostHandGradeScreen } from '@/screens/PostHandGrade';
import { ResumePromptScreen } from '@/screens/ResumePrompt';
import { SessionConfigScreen } from '@/screens/SessionConfig';
import { persistence } from '@/services/persistence';

/**
 * App root.
 *
 * V1 routing scaffold. Implemented: Consent (01), Resume Prompt (02), Session
 * Config (03). Active Hand and downstream screens remain stubbed and land in
 * subsequent vertical slices. See `/design/specs/1_product_requirements.md` §5 and
 * §6, and the wireframes, for the full flow.
 */
export function App() {
  return (
    <>
      <ScrollToTopOnNavigate />
      <Routes>
        <Route path="/" element={<EntryFlowDecider />} />
        <Route path="/consent" element={<ConsentScreen />} />
        <Route path="/resume" element={<ResumePromptScreen />} />
        <Route path="/config" element={<SessionConfigScreen />} />
        <Route path="/play" element={<ActiveHandScreen />} />
        <Route path="/resolution" element={<HandResolutionScreen />} />
        <Route path="/grade" element={<PostHandGradeScreen />} />
        <Route path="/grade/failed" element={<CouldntBeGradedScreen />} />
        <Route path="/session-end" element={<ScreenStub name="Session End (08)" />} />
        <Route path="/finish" element={<ScreenStub name="Finish (09)" />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}

/**
 * Reset window scroll position to the top on every route change. Without
 * this, navigating away from a deep-scrolled grade screen (or any
 * scrollable screen) lands the next screen mid-page. Internal scroll
 * containers within each screen are not affected — only the window's
 * scroll position. Also resets the most likely body scroll container
 * (Tailwind's `<main>` if it ever overflows).
 */
function ScrollToTopOnNavigate() {
  const location = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
    // Also reset document.scrollingElement just in case the layout's
    // scrolling root isn't window (varies by browser when html/body
    // styles change).
    if (document.scrollingElement) {
      document.scrollingElement.scrollTop = 0;
    }
  }, [location.pathname]);
  return null;
}

/**
 * Routes the user to the correct first screen based on persisted state, per
 * PRD §6:
 *   1. No consent recorded → Consent modal (first-launch path)
 *   2. Stored active session → Resume Prompt
 *   3. Otherwise → Session Config (the default entry point)
 *
 * Reads from localStorage synchronously and returns a <Navigate> on first
 * render — no flash of the "wrong" screen, no useEffect needed.
 */
function EntryFlowDecider() {
  const hasConsent = persistence.getConsentFlag();
  if (!hasConsent) return <Navigate to="/consent" replace />;

  const activeSession = persistence.loadActiveSession();
  if (activeSession) return <Navigate to="/resume" replace />;

  return <Navigate to="/config" replace />;
}

/** Placeholder shown for routes that aren't implemented yet. */
function ScreenStub({ name }: { name: string }) {
  return (
    <main className="flex min-h-dvh items-center justify-center bg-surface-app p-8">
      <div className="text-center">
        <p className="mb-1 text-sm uppercase tracking-wider text-text-muted">
          screen stub
        </p>
        <h1 className="text-2xl font-semibold text-text-primary">{name}</h1>
        <p className="mt-2 text-sm text-text-secondary">
          To be implemented in vertical slice.
        </p>
      </div>
    </main>
  );
}
